/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Chart, Line, Pie } from 'react-chartjs-2';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import axios from 'axios';
import http from '../../api/http';
import { fetchControlChartData, fetchControlChartStepNumbers } from '../controlChart/controlChartApi';
import { normalizeControlChartData } from '../controlChart/controlChartData';
import { buildNormalDistributionChartData, buildNormalDistributionOptions, getCapabilityMetrics, normalizeNormalDistribution } from '../normalDistribution/normalDistributionData';
import { fetchNormalDistribution, fetchNormalDistributionFilters } from '../normalDistribution/normalDistributionApi';
import { fetchFirstFailAnalysis, fetchHotspotAnalysis, fetchLateFailureCostAnalysis, fetchMarginRiskAnalysis, fetchCascadeSpreadAnalysis, fetchRetryPressureAnalysis } from '../productionRootCause/productionRootCauseApi';
import { buildRootCauseChartData, getRootCauseMetrics, rootCauseChartOptions } from '../rootCauseAnalysis/rootCauseAnalysisData';
import { fetchRootCauseAnalysis, fetchRootCauseFilters } from '../rootCauseAnalysis/rootCauseAnalysisApi';
import { buildStepChartData, getStepMetrics, normalizeStepFrequencyRows, stepChartOptions } from '../stepFrequency/stepFrequencyData';
import { fetchStepFrequency } from '../stepFrequency/stepFrequencyApi';
import { buildStatusSummaryChartData, getSummaryMetrics } from '../uutStatusSummary/uutStatusSummaryData';
import { fetchUutStatusSummary } from '../uutStatusSummary/uutStatusSummaryApi';
import {
  artavaChartJsAxisColor,
  artavaChartJsGridColor,
  artavaChartJsLegendColor,
  artavaChartJsTickColor,
  artavaChartJsTitleColor,
  artavaChartJsTooltip,
} from '../chartPalette';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Filler,
  annotationPlugin
);

const API_ORIGIN =
  process.env.REACT_APP_API_BASE ||
  (['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:3001'
    : '');

const normalizedApiOrigin = API_ORIGIN.replace(/\/$/, '');
const API_BASE = normalizedApiOrigin.endsWith('/api')
  ? normalizedApiOrigin
  : `${normalizedApiOrigin}/api`;

function toDateOnly(date) {
  if (!date) return '';
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }
  return String(date).slice(0, 10);
}

function getDateRangeProps(startDate, endDate) {
  return {
    startDate: startDate || null,
    endDate: endDate || null,
  };
}

function getRootFilters(startDate, endDate, selectedFamilies = []) {
  return {
    startDate: toDateOnly(startDate),
    endDate: toDateOnly(endDate),
    ateSwVersion: '',
    stationName: '',
    selectedFamilies: selectedFamilies.join(','),
  };
}

function useWidgetLoader(loader, deps) {
  const [state, setState] = useState({ loading: true, error: '', data: null });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;

    const run = async () => {
      setState((current) => ({ ...current, loading: true, error: '' }));
      try {
        const data = await loader();
        if (!active) return;
        setState({ loading: false, error: '', data });
      } catch (error) {
        if (!active) return;
        setState({
          loading: false,
          error: error?.message || 'Failed to load widget data.',
          data: null,
        });
      }
    };

    run();
    return () => {
      active = false;
    };
  }, deps);

  return state;
}

function WidgetState({ styles, message }) {
  return <div className={styles.placeholderContent}>{message}</div>;
}

function WidgetStats({ styles, stats }) {
  return (
    <div className={styles.widgetMiniStats}>
      {stats.filter(Boolean).map((stat) => (
        <div className={styles.widgetMiniStat} key={stat.label}>
          <span>{stat.label}</span>
          <strong>{stat.value}</strong>
        </div>
      ))}
    </div>
  );
}

function WidgetFooter({ styles, navigate, path, label = 'Open full page' }) {
  return (
    <div className={styles.widgetLinkRow}>
      <button type="button" className={styles.widgetLinkButton} onClick={() => navigate(path)}>
        {label}
      </button>
    </div>
  );
}

function topRows(rows, count = 8) {
  return Array.isArray(rows) ? rows.slice(0, count) : [];
}

function createBarOptions(yLabel, { legend = false, horizontal = false } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: legend,
        position: 'bottom',
        labels: {
          color: artavaChartJsLegendColor,
        },
      },
      tooltip: artavaChartJsTooltip,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: yLabel ? { display: true, text: yLabel, color: artavaChartJsTitleColor } : undefined,
        ticks: { precision: 0, color: artavaChartJsTickColor },
        grid: { color: artavaChartJsGridColor },
        border: { color: artavaChartJsAxisColor },
      },
      x: {
        grid: { color: artavaChartJsGridColor },
        border: { color: artavaChartJsAxisColor },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          color: artavaChartJsTickColor,
        },
      },
    },
  };
}

function createComposedOptions(leftLabel, rightLabel) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: artavaChartJsLegendColor,
        },
      },
      tooltip: artavaChartJsTooltip,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: leftLabel ? { display: true, text: leftLabel, color: artavaChartJsTitleColor } : undefined,
        ticks: { color: artavaChartJsTickColor },
        grid: { color: artavaChartJsGridColor },
        border: { color: artavaChartJsAxisColor },
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        title: rightLabel ? { display: true, text: rightLabel, color: artavaChartJsTitleColor } : undefined,
        ticks: { color: artavaChartJsTickColor },
        border: { color: artavaChartJsAxisColor },
      },
      x: {
        grid: { color: artavaChartJsGridColor },
        border: { color: artavaChartJsAxisColor },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          color: artavaChartJsTickColor,
        },
      },
    },
  };
}

function ControlChartWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(async () => {
    const params = {
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
      selectedFamilies: selectedFamilies.join(',') || undefined,
    };
    const steps = await fetchControlChartStepNumbers(params);
    const selectedStep = [...(steps || [])]
      .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0))[0];
    if (!selectedStep?.stepNumber) return null;

    const rows = normalizeControlChartData(await fetchControlChartData({
      ...params,
      stepNumber: selectedStep.stepNumber,
    }));

    return rows.find((row) => String(row.stepNumber) === String(selectedStep.stepNumber)) || null;
  }, [endDate, selectedFamilies, startDate]);

  const chartData = useMemo(() => {
    if (!data?.values?.length) return null;
    const values = data.values.slice(0, 30);
    return {
      labels: values.map((_, index) => index + 1),
      datasets: [
        {
          label: 'Measure value',
          data: values,
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.16)',
          borderWidth: 2,
          tension: 0.28,
          fill: true,
          pointRadius: 0,
        },
        {
          label: 'Center line',
          data: values.map(() => Number(data.xBar) || 0),
          borderColor: '#0f766e',
          borderDash: [6, 6],
          pointRadius: 0,
        },
      ],
    };
  }, [data]);

  if (loading) return <WidgetState styles={styles} message="Loading control-chart preview..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!data || !chartData) return <WidgetState styles={styles} message="No control-chart step is available for this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Step', value: `#${data.stepNumber}` },
          { label: 'Samples', value: data.values.length },
          { label: 'Mean', value: Number(data.xBar || 0).toFixed(2) },
          { label: 'Cpk', value: Number.isFinite(Number(data.cpk)) ? Number(data.cpk).toFixed(2) : 'N/A' },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Line data={chartData} options={createBarOptions('Measure')} />
      </div>
      <p className={styles.widgetCaption}>
        Showing the most populated control-chart step: {data.stepNumber} {data.stepName ? `- ${data.stepName}` : ''}.
      </p>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function UutStatusSummaryWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchUutStatusSummary({ ...getDateRangeProps(startDate, endDate), selectedSerialNumbers: [], selectedFamilies }),
    [endDate, selectedFamilies, startDate]
  );

  const sortedRows = useMemo(
    () => [...(data || [])]
      .sort((a, b) => (
        (Number(b.passCount) + Number(b.failCount) + Number(b.stopCount) + Number(b.uncompleteCount))
        - (Number(a.passCount) + Number(a.failCount) + Number(a.stopCount) + Number(a.uncompleteCount))
      ))
      .slice(0, 6),
    [data]
  );

  const chartData = useMemo(() => buildStatusSummaryChartData(sortedRows), [sortedRows]);
  const metrics = useMemo(() => getSummaryMetrics(data || []).slice(0, 4), [data]);

  if (loading) return <WidgetState styles={styles} message="Loading UUT status summary..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!sortedRows.length) return <WidgetState styles={styles} message="No UUT status records matched this date range." />;

  return (
    <>
      <WidgetStats styles={styles} stats={metrics.map((metric) => ({ label: metric.label, value: metric.value }))} />
      <div className={styles.widgetChartWide}>
        <Bar data={chartData} options={{ ...createBarOptions('Status count', { legend: true }), plugins: { legend: { position: 'bottom' } } }} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function RootCausePredictionWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(async () => {
    const filters = await fetchRootCauseFilters({ selectedFamilies });
    const primaryStep = [...(filters.stepNumbers || [])]
      .sort((a, b) => (Number(b.failSessions) || 0) - (Number(a.failSessions) || 0))[0];
    if (!primaryStep?.stepNumber) return null;

    const analysis = await fetchRootCauseAnalysis({
      selectedStepNumber: primaryStep.stepNumber,
      startDate,
      endDate,
      ateSwVersion: '',
      selectedSerialNumbers: [],
      selectedFamilies,
    });

    return {
      primaryStep,
      analysis,
    };
  }, [endDate, selectedFamilies, startDate]);

  const candidates = topRows(data?.analysis?.candidates || [], 6);
  const chartData = useMemo(() => buildRootCauseChartData(candidates), [candidates]);
  const metrics = useMemo(
    () => getRootCauseMetrics(data?.analysis?.candidates || [], data?.analysis?.summary || {}).slice(0, 4),
    [data]
  );

  if (loading) return <WidgetState styles={styles} message="Loading root-cause analyzer..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!data?.primaryStep || !candidates.length) return <WidgetState styles={styles} message="No root-cause candidates were found for this date range." />;

  return (
    <>
      <WidgetStats styles={styles} stats={metrics.map((metric) => ({ label: metric.label, value: metric.value }))} />
      <div className={styles.widgetChartWide}>
        <Bar
          data={chartData}
          options={{
            ...rootCauseChartOptions,
            maintainAspectRatio: false,
            plugins: { ...rootCauseChartOptions.plugins, legend: { display: false } },
          }}
        />
      </div>
      <p className={styles.widgetCaption}>
        Using primary step {data.primaryStep.stepNumber} {data.primaryStep.stepName ? `- ${data.primaryStep.stepName}` : ''} as the target failure trigger.
      </p>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function StepFrequencyWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    async () => normalizeStepFrequencyRows(await fetchStepFrequency({
      startDate,
      endDate,
      ateSwVersion: '',
      selectedStepNumbers: [],
      selectedFamilies,
    })),
    [endDate, selectedFamilies, startDate]
  );

  const rows = useMemo(
    () => [...(data || [])]
      .filter((item) => Number(item.failCount) > 0 || item.avgMeasureValue !== null)
      .sort((a, b) => b.failPercentage - a.failPercentage),
    [data]
  );
  const chartData = useMemo(() => buildStepChartData(topRows(rows, 8)), [rows]);
  const metrics = useMemo(() => getStepMetrics(rows).slice(0, 4), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading step-frequency analysis..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No step-frequency data was found for this date range." />;

  return (
    <>
      <WidgetStats styles={styles} stats={metrics.map((metric) => ({ label: metric.label, value: metric.value }))} />
      <div className={styles.widgetChartWide}>
        <Bar data={chartData} options={stepChartOptions('Fail % by step')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function RootCauseAlarmsWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(async () => {
    const params = {};
    const start = toDateOnly(startDate);
    const end = toDateOnly(endDate);
    if (start) params.startDate = start;
    if (end) params.endDate = end;
    if (selectedFamilies.length) params.selectedFamilies = selectedFamilies.join(',');
    const response = await axios.get(`${API_BASE}/root-cause-alarms`, { params });
    return response.data;
  }, [endDate, selectedFamilies, startDate]);

  const alarms = topRows(data?.alarms || [], 6);
  const chartData = useMemo(() => ({
    labels: alarms.map((alarm) => `Step ${alarm.stepNumber}`),
    datasets: [
      {
        label: 'Severity score',
        data: alarms.map((alarm) => Number(alarm.severityScore) || 0),
        backgroundColor: alarms.map((alarm) => {
          if (alarm.severityLevel === 'critical') return 'rgba(190, 24, 93, 0.78)';
          if (alarm.severityLevel === 'high') return 'rgba(214, 69, 69, 0.78)';
          if (alarm.severityLevel === 'medium') return 'rgba(242, 153, 74, 0.78)';
          return 'rgba(91, 141, 239, 0.72)';
        }),
      },
    ],
  }), [alarms]);

  if (loading) return <WidgetState styles={styles} message="Loading alarm queue..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!alarms.length) return <WidgetState styles={styles} message="No root-cause alarms matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Active alarms', value: data?.summary?.alarmsReturned || 0 },
          { label: 'Steps analyzed', value: data?.summary?.totalStepsAnalyzed || 0 },
          { label: 'Failed products', value: data?.summary?.failedProductSessions || 0 },
          { label: 'Top severity', value: alarms[0]?.severityLevel || '-' },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Bar data={chartData} options={createBarOptions('Severity score')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function MlRunsWidget({ styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(async () => {
    const latest = await http('/api/ml/runs/latest');
    const runId = latest?.run?.runId;
    if (!runId) return null;
    const summary = await http(`/api/ml/runs/${runId}/summary`);
    return { latest: latest.run, summary };
  }, []);

  const chartData = useMemo(() => ({
    labels: ['Pred PASS', 'Pred FAIL'],
    datasets: [
      {
        data: [
          Number(data?.summary?.counts?.passCount) || 0,
          Number(data?.summary?.counts?.failCount) || 0,
        ],
        backgroundColor: ['#2f9e44', '#d64545'],
        borderWidth: 0,
      },
    ],
  }), [data]);

  if (loading) return <WidgetState styles={styles} message="Loading latest ML run..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!data?.latest || !data?.summary) return <WidgetState styles={styles} message="No ML runs are available yet." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Run ID', value: data.latest.runId?.slice(-8) || '-' },
          { label: 'Samples', value: data.summary.counts?.total || 0 },
          { label: 'Pred fail', value: data.summary.counts?.failCount || 0 },
          { label: 'Mode', value: data.summary.mode || 'label' },
        ]}
      />
      <div className={styles.widgetChartTall}>
        <Pie data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} label="Open ML workspace" />
    </>
  );
}

function ProcessCapabilityWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(async () => {
    const filters = await fetchNormalDistributionFilters({ selectedFamilies });
    const step = [...(filters.stepNumbers || [])]
      .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0))[0];
    if (!step?.stepNumber) return null;

    const response = await fetchNormalDistribution({
      selectedStepNumber: step.stepNumber,
      startDate,
      endDate,
      selectedAteSwVersion: '',
      selectedTestspec: '',
      lsl: '',
      usl: '',
      selectedFamilies,
    });

    return normalizeNormalDistribution(response);
  }, [endDate, selectedFamilies, startDate]);

  const chartData = useMemo(() => buildNormalDistributionChartData(data), [data]);
  const chartOptions = useMemo(() => {
    const options = buildNormalDistributionOptions(data);
    return {
      ...options,
      maintainAspectRatio: false,
      plugins: {
        ...options.plugins,
        title: { display: false },
        legend: { position: 'bottom' },
      },
    };
  }, [data]);
  const metrics = useMemo(() => getCapabilityMetrics(data).slice(0, 4), [data]);

  if (loading) return <WidgetState styles={styles} message="Loading process capability preview..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!data || !chartData?.datasets?.length) return <WidgetState styles={styles} message="No numeric distribution is available for this date range." />;

  return (
    <>
      <WidgetStats styles={styles} stats={metrics.map((metric) => ({ label: metric.label, value: metric.value }))} />
      <div className={styles.widgetChartWide}>
        <Chart type="bar" data={chartData} options={chartOptions} />
      </div>
      <p className={styles.widgetCaption}>
        Previewing step {data.stepNumber} {data.stepName ? `- ${data.stepName}` : ''}.
      </p>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function FirstFailWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchFirstFailAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const rows = topRows(data?.rows || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => `Step ${row.stepNumber}`),
    datasets: [
      {
        label: 'First fails',
        data: rows.map((row) => Number(row.firstFailSessions) || 0),
        backgroundColor: 'rgba(36, 116, 95, 0.72)',
      },
      {
        label: 'Root score',
        data: rows.map((row) => Number(row.score) || 0),
        backgroundColor: 'rgba(17, 24, 39, 0.72)',
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading first-fail fingerprint..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No first-fail patterns matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Sessions', value: data?.summary?.sessions || 0 },
          { label: 'Failed sessions', value: data?.summary?.failedSessions || 0 },
          { label: 'Patterns', value: data?.summary?.firstFailPatterns || 0 },
          { label: 'Top share', value: `${data?.summary?.topFirstFailShare || 0}%` },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Bar data={chartData} options={{ ...createBarOptions('Sessions', { legend: true }), plugins: { legend: { position: 'bottom' } } }} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function MarginRiskWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchMarginRiskAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const rows = topRows(data?.rows || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => row.chartLabel || `Step ${row.stepNumber}`),
    datasets: [
      {
        type: 'bar',
        label: 'Risk score',
        data: rows.map((row) => Number(row.riskScore) || 0),
        backgroundColor: 'rgba(17, 24, 39, 0.72)',
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Near-limit pass %',
        data: rows.map((row) => Number(row.passNearLimitRate ?? row.nearLimitRate) || 0),
        borderColor: '#24745f',
        backgroundColor: '#24745f',
        yAxisID: 'y1',
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading limit-margin risk..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No limit-margin risks matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Numeric rows', value: data?.summary?.numericRows || 0 },
          { label: 'Comparable rows', value: data?.summary?.comparableRows || 0 },
          { label: 'Risky steps', value: data?.summary?.riskySteps || 0 },
          { label: 'Top score', value: data?.summary?.topRiskScore || 0 },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Chart type="bar" data={chartData} options={createComposedOptions('Risk score', 'Near-limit %')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function HotspotsWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchHotspotAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const activeGroup = useMemo(() => {
    const groups = ['stations', 'operators', 'fixtures', 'versions', 'hours'];
    return groups.reduce((best, key) => {
      const bestScore = Number((data?.[best] || [])[0]?.score) || 0;
      const currentScore = Number((data?.[key] || [])[0]?.score) || 0;
      return currentScore > bestScore ? key : best;
    }, 'stations');
  }, [data]);
  const rows = topRows(data?.[activeGroup] || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => String(row.label)),
    datasets: [
      {
        label: 'Failure lift',
        data: rows.map((row) => Number(row.lift) || 0),
        backgroundColor: rows.map((row) => (Number(row.lift) >= 1.5 ? 'rgba(138, 63, 93, 0.78)' : 'rgba(36, 116, 95, 0.72)')),
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading production hotspots..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No hotspot groups matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Sessions', value: data?.summary?.sessions || 0 },
          { label: 'Failures', value: data?.summary?.failures || 0 },
          { label: 'Baseline', value: `${data?.summary?.baselineFailureRate || 0}%` },
          { label: 'Group', value: activeGroup },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Bar data={chartData} options={createBarOptions('Failure lift')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function CascadeWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchCascadeSpreadAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const rows = topRows(data?.rows || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => `Step ${row.stepNumber}`),
    datasets: [
      {
        type: 'bar',
        label: 'Avg downstream fails',
        data: rows.map((row) => Number(row.avgDownstreamFails) || 0),
        backgroundColor: 'rgba(138, 63, 93, 0.78)',
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Cascade-heavy %',
        data: rows.map((row) => Number(row.cascadeHeavyRate) || 0),
        borderColor: '#24745f',
        backgroundColor: '#24745f',
        yAxisID: 'y1',
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading cascade spread..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No cascade patterns matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Failed sessions', value: data?.summary?.failedSessions || 0 },
          { label: 'Patterns', value: data?.summary?.firstFailPatterns || 0 },
          { label: 'Top score', value: data?.summary?.topCascadeScore || 0 },
          { label: 'Top avg', value: data?.summary?.topAvgDownstreamFails || 0 },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Chart type="bar" data={chartData} options={createComposedOptions('Downstream fails', 'Cascade-heavy %')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function RetryPressureWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchRetryPressureAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const rows = topRows(data?.rows || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => String(row.testStepNumber)),
    datasets: [
      {
        type: 'bar',
        label: 'Total retries',
        data: rows.map((row) => Number(row.totalRetries) || 0),
        backgroundColor: 'rgba(138, 63, 93, 0.78)',
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Retry rate %',
        data: rows.map((row) => Number(row.retryRate) || 0),
        borderColor: '#24745f',
        backgroundColor: '#24745f',
        yAxisID: 'y1',
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading retry pressure..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No retry-heavy sections matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Section rows', value: data?.summary?.sectionRows || 0 },
          { label: 'Sections', value: data?.summary?.sectionsAnalyzed || 0 },
          { label: 'Retries', value: data?.summary?.totalRetries || 0 },
          { label: 'Top score', value: data?.summary?.topRetryScore || 0 },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Chart type="bar" data={chartData} options={createComposedOptions('Retries', 'Retry rate %')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

function LateFailureWidget({ startDate, endDate, selectedFamilies, styles, navigate, path }) {
  const { loading, error, data } = useWidgetLoader(
    () => fetchLateFailureCostAnalysis(getRootFilters(startDate, endDate, selectedFamilies)),
    [endDate, selectedFamilies, startDate]
  );

  const rows = topRows(data?.rows || [], 8);
  const chartData = useMemo(() => ({
    labels: rows.map((row) => `Step ${row.stepNumber}`),
    datasets: [
      {
        type: 'bar',
        label: 'Avg fail position %',
        data: rows.map((row) => Number(row.avgPositionPct) || 0),
        backgroundColor: 'rgba(17, 24, 39, 0.72)',
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Late-fail %',
        data: rows.map((row) => Number(row.lateFailureRate) || 0),
        borderColor: '#24745f',
        backgroundColor: '#24745f',
        yAxisID: 'y1',
      },
    ],
  }), [rows]);

  if (loading) return <WidgetState styles={styles} message="Loading late-stage failure cost..." />;
  if (error) return <WidgetState styles={styles} message={error} />;
  if (!rows.length) return <WidgetState styles={styles} message="No late-failure patterns matched this date range." />;

  return (
    <>
      <WidgetStats
        styles={styles}
        stats={[
          { label: 'Failed sessions', value: data?.summary?.failedSessions || 0 },
          { label: 'Tracked steps', value: data?.summary?.failedStepsTracked || 0 },
          { label: 'Top late rate', value: `${data?.summary?.topLateFailureRate || 0}%` },
          { label: 'Top position', value: `${data?.summary?.topAvgPositionPct || 0}%` },
        ]}
      />
      <div className={styles.widgetChartWide}>
        <Chart type="bar" data={chartData} options={createComposedOptions('Avg position %', 'Late-fail %')} />
      </div>
      <WidgetFooter styles={styles} navigate={navigate} path={path} />
    </>
  );
}

export default function ProductionDashboardWidgetBody({
  id,
  startDate,
  endDate,
  selectedFamilies = [],
  styles,
  navigate,
  path,
}) {
  if (id === 'control_chart_widget') {
    return <ControlChartWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'uut_status_widget') {
    return <UutStatusSummaryWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'root_cause_widget') {
    return <RootCausePredictionWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'step_frequency_widget') {
    return <StepFrequencyWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'root_cause_alarms_widget') {
    return <RootCauseAlarmsWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'ml_runs_widget') {
    return <MlRunsWidget styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'process_capability_widget') {
    return <ProcessCapabilityWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'first_fail_widget') {
    return <FirstFailWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'margin_risk_widget') {
    return <MarginRiskWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'hotspots_widget') {
    return <HotspotsWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'cascade_widget') {
    return <CascadeWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'retry_pressure_widget') {
    return <RetryPressureWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }
  if (id === 'late_failure_widget') {
    return <LateFailureWidget startDate={startDate} endDate={endDate} selectedFamilies={selectedFamilies} styles={styles} navigate={navigate} path={path} />;
  }

  return <WidgetState styles={styles} message="This dashboard widget is not available yet." />;
}
